import { useEffect, useRef } from 'react';

interface IUseSizeAsCSSVariableProps {
  cssVariableName: string;
  size?: 'height' | 'width';
}

interface IUseSizeAsCSSVariableReturn<T extends HTMLElement> {
  ref: React.RefObject<T>;
}

function useSizeAsCSSVariable<T extends HTMLElement>({
  cssVariableName,
  size = 'height',
}: IUseSizeAsCSSVariableProps): IUseSizeAsCSSVariableReturn<T> {
  const ref = useRef<T>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    // Update the CSS variable with the current size of the element.
    function updateSize(): void {
      if (ref.current) {
        const currentSize = ref.current.getBoundingClientRect()[size] ?? 0;
        document.documentElement.style.setProperty(cssVariableName, `${currentSize}px`);
      }
    }

    // Clean up previous observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    // Use requestAnimationFrame to ensure the element is rendered (especially for portals)
    const rafId = requestAnimationFrame(() => {
      if (ref.current) {
        updateSize();
        resizeObserverRef.current = new ResizeObserver(updateSize);
        resizeObserverRef.current.observe(ref.current);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [cssVariableName, size]);

  return { ref };
}

export default useSizeAsCSSVariable;
